import { AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import type { PreviewCreateFromOrdersOutput } from '@/lib/api/subscriptions';
import { formatAmount, formatDate } from '@/lib/format';

interface ReviewStepProps {
  preview?: PreviewCreateFromOrdersOutput;
  isLoading: boolean;
}

export function ReviewStep({ preview, isLoading }: ReviewStepProps) {
  const { t } = useTranslation('subscriptions');

  if (isLoading || !preview) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }

  const { accepted, rejected, cappedAt } = preview;

  return (
    <div className="space-y-4">
      {cappedAt ? (
        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            {t('createFromOrders.review.capped', { count: cappedAt })}
          </AlertDescription>
        </Alert>
      ) : null}

      {accepted.length > 0 ? (
        <div className="space-y-2">
          <p className="text-sm font-medium">
            {t('createFromOrders.review.willCreate', { count: accepted.length })}
          </p>
          <div className="max-h-64 overflow-y-auto rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('createFromOrders.review.customer')}</TableHead>
                  <TableHead>{t('createFromOrders.review.quantity')}</TableHead>
                  <TableHead>{t('createFromOrders.review.amount')}</TableHead>
                  <TableHead>{t('createFromOrders.review.card')}</TableHead>
                  <TableHead>
                    {t('createFromOrders.review.firstCharge')}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {accepted.map((row) => (
                  <TableRow key={row.orderId}>
                    <TableCell className="max-w-[180px] truncate">
                      {row.customerEmail ?? row.customerId}
                    </TableCell>
                    <TableCell>{row.quantity}</TableCell>
                    <TableCell>{formatAmount(row.amount, row.currency)}</TableCell>
                    <TableCell>
                      {row.card?.last4
                        ? `${row.card.brand ?? ''} •••• ${row.card.last4}`.trim()
                        : t('createFromOrders.review.noCard')}
                    </TableCell>
                    <TableCell>{formatDate(row.billingCycleAnchor, 'PP')}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      ) : (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            {t('createFromOrders.review.noneEligible')}
          </AlertDescription>
        </Alert>
      )}

      {rejected.length > 0 ? (
        <div className="space-y-2">
          <p className="text-sm font-medium">
            {t('createFromOrders.review.willSkip', { count: rejected.length })}
          </p>
          <div className="max-h-40 overflow-y-auto rounded-md border">
            <Table>
              <TableBody>
                {rejected.map((row) => (
                  <TableRow key={row.orderId}>
                    <TableCell className="font-mono text-xs">{row.orderId}</TableCell>
                    <TableCell>
                      <Badge variant="secondary">
                        {t(`createFromOrders.reasons.${row.reason}`, { defaultValue: row.reason })}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      ) : null}
    </div>
  );
}
